// partners/adminView/pages/A5_AdminPartnerDetail.jsx
// Partner detail — stats, bank editing, toggle active, register fund account.

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetAdminPartnerDetailQuery,
  useUpdatePartnerMutation,
  useRegisterFundAccountMutation,
} from "../../../apis/adminApiSlice";
import AdminLayout from "./A0_AdminLayout";
import { capturePartnerEvent } from "../../../utils/analytics";

export default function AdminPartnerDetail() {
  const { id }       = useParams();
  const navigate     = useNavigate();
  const { data: partner, isLoading } = useGetAdminPartnerDetailQuery(id);
  const [updatePartnerDetails, { isLoading: savingDetails }] = useUpdatePartnerMutation();
  const [togglePartnerState,  { isLoading: togglingState }]  = useUpdatePartnerMutation();
  const [registerFundAccount, { isLoading: registering }] = useRegisterFundAccountMutation();

  const [bankName,   setBankName]   = useState("");
  const [bankNumber, setBankNumber] = useState("");
  const [bankIFSC,   setBankIFSC]   = useState("");
  const [upiId,      setUpiId]      = useState("");
  const [payMethod,  setPayMethod]  = useState("bank_account");
  const [feedback,   setFeedback]   = useState("");
  const [thrVal,     setThrVal]     = useState("");
  const viewedRef = useRef(false);

  useEffect(() => {
    if (partner && !viewedRef.current) {
      viewedRef.current = true;
      capturePartnerEvent("partner_admin_partner_viewed", {
        partner_id: id,
        is_active: partner.is_active,
      });
    }
  }, [partner, id]);

  useEffect(() => {
    if (!partner) return;
    setBankName(partner.bank_account_name || "");
    setBankNumber(partner.bank_account_number || "");
    setBankIFSC(partner.bank_ifsc || "");
    setUpiId(partner.upi_id || "");
    setPayMethod(partner.payout_method || "bank_account");
    setThrVal(
      partner.payout_threshold_usd != null
        ? String(partner.payout_threshold_usd)
        : String(partner.effective_payout_threshold_usd || partner.default_payout_threshold_usd || 80)
    );
  }, [partner]);

  if (isLoading)
    return <AdminLayout><div className="text-mnf-text-muted text-sm">Loading...</div></AdminLayout>;
  if (!partner)
    return <AdminLayout><div className="text-mnf-error text-sm">Partner not found.</div></AdminLayout>;

  const p = partner;
  const hasUnsavedPaymentChanges =
    (bankName || "") !== (p.bank_account_name || "") ||
    (bankNumber || "") !== (p.bank_account_number || "") ||
    (bankIFSC || "") !== (p.bank_ifsc || "") ||
    (upiId || "") !== (p.upi_id || "") ||
    (payMethod || "bank_account") !== (p.payout_method || "bank_account");

  const handleUpdateBank = async () => {
    if (payMethod === "bank_account" && (!bankNumber || !bankIFSC)) {
      setFeedback("Bank account number and IFSC are required for bank payout method.");
      return;
    }
    if (payMethod === "upi" && !upiId) {
      setFeedback("UPI ID is required when payout method is UPI.");
      return;
    }
    try {
      await updatePartnerDetails({
        id,
        bank_account_name:   bankName.trim()   || undefined,
        bank_account_number: bankNumber.trim() || undefined,
        bank_ifsc:           bankIFSC.trim()   || undefined,
        upi_id:              upiId.trim()      || undefined,
        payout_method:       payMethod,
      }).unwrap();
      capturePartnerEvent("partner_admin_partner_payment_updated", { partner_id: id });
      setFeedback("Payment details updated.");
    } catch (e) {
      setFeedback(`Error: ${e?.data?.detail || "Update failed."}`);
    }
  };

  const handleToggleActive = async () => {
    try {
      await togglePartnerState({ id, is_active: !p.is_active }).unwrap();
      capturePartnerEvent("partner_admin_partner_toggled", {
        partner_id: id,
        is_active: !p.is_active,
      });
      setFeedback(`Partner ${p.is_active ? "deactivated" : "reactivated"}.`);
    } catch {
      setFeedback("Toggle failed.");
    }
  };

  const handleSaveThreshold = async () => {
    try {
      const trimmed = thrVal.trim();
      await updatePartnerDetails({
        id,
        payout_threshold_usd: trimmed === "" ? null : Number(trimmed),
      }).unwrap();
      capturePartnerEvent("partner_admin_partner_threshold_updated", { partner_id: id });
      setFeedback("Payout threshold updated.");
    } catch {
      setFeedback("Threshold update failed.");
    }
  };

  const handleRegisterFundAccount = async () => {
    if (hasUnsavedPaymentChanges) {
      setFeedback("Please click 'Save payment details' first, then register fund account.");
      return;
    }
    if (p.payout_method === "upi" && !p.upi_id) {
      setFeedback("UPI payout method is selected, but UPI ID is missing.");
      return;
    }
    if (
      p.payout_method === "bank_account" &&
      (!p.bank_account_number || !p.bank_ifsc)
    ) {
      setFeedback("Bank payout method is selected, but account number/IFSC is missing.");
      return;
    }
    if (!window.confirm("Register fund account on RazorpayX? This creates a live contact/fund account."))
      return;
    try {
      await registerFundAccount(id).unwrap();
      capturePartnerEvent("partner_admin_partner_fund_account_registered", {
        partner_id: id,
        success: true,
      });
      setFeedback("Fund account registered successfully.");
    } catch (e) {
      capturePartnerEvent("partner_admin_partner_fund_account_registered", {
        partner_id: id,
        success: false,
      });
      setFeedback(`Error: ${e?.data?.error || "Registration failed."}`);
    }
  };

  const row = (label, value, cls) => (
    <div className="flex py-2 border-b border-white/[0.04]">
      <div className="w-44 text-xs text-mnf-text-muted shrink-0">{label}</div>
      <div className={`text-sm ${cls || "text-mnf-text-primary"}`}>{value ?? "—"}</div>
    </div>
  );

  return (
    <AdminLayout>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="text-mnf-text-muted hover:text-mnf-cyan text-sm transition-colors"
        >
          ← Back
        </button>
        <h1 className="font-poppins text-2xl font-bold">{p.name}</h1>
        <span
          className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
            p.is_active ? "bg-mnf-success/20 text-mnf-success" : "bg-mnf-error/20 text-mnf-error"
          }`}
        >
          {p.is_active ? "Active" : "Inactive"}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-4">
        {/* Partner info */}
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg p-6">
          <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-4">
            Partner Info
          </h2>
          {row("Referral code",     <span className="font-mono text-mnf-cyan">{p.referral_code}</span>)}
          {row("Discount code",     p.discount_code)}
          {row("Type",              p.partner_type === "creative_ambassador" ? "Creative Ambassador" : "Ecosystem")}
          {row("Tier",              p.tier)}
          {row("Commission rate",   `${p.commission_rate}%`)}
          {row(
            "Payout threshold",
            `$${Number(p.effective_payout_threshold_usd || p.default_payout_threshold_usd || 80).toLocaleString("en-US", { maximumFractionDigits: 2 })}${p.payout_threshold_usd != null ? " (custom)" : " (default)"}`
          )}
          {row("Total clicks",      p.total_clicks?.toLocaleString("en-IN"))}
          {row("Total signups",     p.total_signups?.toLocaleString("en-IN"))}
          {row("Total conversions", p.total_conversions?.toLocaleString("en-IN"))}
          {row("Total earnings",    `$${Number(p.total_earnings || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`)}
          {row("Total paid",        `$${Number(p.total_paid    || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`)}
          {row("Activated at",      p.activated_at ? new Date(p.activated_at).toLocaleDateString("en-IN") : "—")}

          <div className="mt-5">
            <button
              onClick={handleToggleActive}
              disabled={togglingState}
              className={`text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill border transition-colors disabled:opacity-50 ${
                p.is_active
                  ? "border-mnf-error/40 text-mnf-error hover:bg-mnf-error/10"
                  : "border-mnf-success/40 text-mnf-success hover:bg-mnf-success/10"
              }`}
            >
              {p.is_active ? "Deactivate" : "Reactivate"}
            </button>
          </div>
        </div>

        {/* Payout / bank details */}
        <div className="bg-mnf-dark-2 border border-white/[0.08] rounded-mnf-lg p-6">
          <h2 className="font-poppins text-sm font-semibold text-mnf-text-muted uppercase tracking-widest mb-4">
            Payout Details
          </h2>
          {row("Contact ID",       p.razorpay_contact_id      || "Not registered")}
          {row("Fund account ID",  p.razorpay_fund_account_id || "Not registered")}
          {row("Payout method",    p.payout_method)}
          {row("Bank name",        p.bank_account_name)}
          {row("Bank number",      p.bank_account_number ? `****${p.bank_account_number.slice(-4)}` : "—")}
          {row("IFSC",             p.bank_ifsc)}
          {row("UPI ID",           p.upi_id)}
          {row("Has PAN",          p.has_pan ? "Yes" : "No")}

          <div className="mt-4 pt-4 border-t border-white/[0.06]">
            <div className="font-poppins text-xs font-semibold text-mnf-text-muted uppercase tracking-wider mb-2">
              Payout threshold (USD)
            </div>
            <p className="text-[11px] text-mnf-text-muted mb-2 leading-relaxed">
              {p.payout_threshold_note || "Minimum unpaid commission this partner must reach before becoming eligible for monthly payout."}
            </p>
            <input
              type="number"
              min="0"
              step="0.01"
              value={thrVal}
              onChange={(e) => setThrVal(e.target.value)}
              className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-primary focus:outline-none focus:border-mnf-cyan"
            />
            <p className="text-[10px] text-mnf-text-muted mt-1">
              Program default: ${Number(p.default_payout_threshold_usd || 80).toLocaleString("en-US", { maximumFractionDigits: 2 })}. Clear and save to reset to default.
            </p>
            <button
              type="button"
              onClick={handleSaveThreshold}
              disabled={savingDetails}
              className="mt-2 text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill border border-mnf-cyan/30 text-mnf-cyan hover:bg-mnf-cyan/10 disabled:opacity-50"
            >
              Save threshold
            </button>
          </div>

          <div className="mt-5">
            <button
              onClick={handleRegisterFundAccount}
              disabled={registering || !!p.razorpay_fund_account_id}
              className="text-xs font-poppins font-semibold px-4 py-2 rounded-mnf-pill bg-mnf-gradient text-mnf-btn-text disabled:opacity-40 transition-opacity"
            >
              {p.razorpay_fund_account_id
                ? "Fund account registered ✓"
                : registering
                ? "Registering…"
                : "Register Fund Account"}
            </button>
          </div>

          {/* Bank edit form */}
          <div className="mt-6 pt-5 border-t border-white/[0.06]">
            <div className="font-poppins text-xs font-semibold text-mnf-text-muted uppercase tracking-wider mb-3">
              Update Payment Details
            </div>
            <div className="space-y-2">
              <div className="mb-2">
                <label className="block text-[11px] text-mnf-text-muted mb-1">
                  Preferred payout method (used for Razorpay fund account)
                </label>
                <select
                  value={payMethod}
                  onChange={(e) => setPayMethod(e.target.value)}
                  className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-secondary focus:outline-none focus:border-mnf-cyan"
                >
                  <option value="bank_account">Bank Account</option>
                  <option value="upi">UPI</option>
                </select>
              </div>

              <div className="pt-2 pb-1 text-[11px] text-mnf-text-muted uppercase tracking-wider">
                {payMethod === "bank_account" ? "Bank details" : "UPI details"}
              </div>
              {payMethod === "bank_account" &&
                [
                  [bankName, setBankName, "Account holder name"],
                  [bankNumber, setBankNumber, "Account number"],
                ].map(([val, setter, placeholder]) => (
                  <input
                    key={placeholder}
                    value={val}
                    onChange={(e) => setter(e.target.value)}
                    placeholder={placeholder}
                    className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-primary focus:outline-none focus:border-mnf-cyan"
                  />
                ))}
              {payMethod === "bank_account" && (
                <input
                  value={bankIFSC}
                  onChange={(e) => setBankIFSC(e.target.value.toUpperCase())}
                  placeholder="IFSC code"
                  className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-primary focus:outline-none focus:border-mnf-cyan"
                />
              )}
              {payMethod === "upi" && (
                <input
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  placeholder="UPI ID (example@upi)"
                  className="w-full px-3 py-2 bg-mnf-dark-3 border border-white/[0.08] rounded-mnf text-sm text-mnf-text-primary focus:outline-none focus:border-mnf-cyan"
                />
              )}
              <button
                onClick={handleUpdateBank}
                disabled={savingDetails}
                className="w-full font-poppins text-xs font-semibold py-2 rounded-mnf-pill bg-mnf-dark-4 border border-mnf-cyan/30 text-mnf-cyan hover:bg-mnf-cyan/10 transition-colors disabled:opacity-50"
              >
                {savingDetails ? "Saving…" : "Save payment details"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {feedback && <div className="text-sm text-mnf-cyan">{feedback}</div>}
    </AdminLayout>
  );
}
